import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification, Modal } from 'antd';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  code: number;
  data: any;
  msg: string;
}

/** JsonResponseMiddleware 内层：200 或 0 均视为业务成功（部分旧接口仍返回 0） */
const isInnerBizSuccess = (code: unknown): boolean => code === 200 || code === 0;

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const resAny = res as unknown as ResponseStructure;
      const { code, data, msg } = resAny;

      if (code !== 200) {
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { code, msg, data };
        throw error;
      }

      // JsonResponseMiddleware：外层 { code:200, data: <Controller 原 JSON> }，业务错误在内层 data.code / data.msg
      if (
        code === 200 &&
        data != null &&
        typeof data === 'object' &&
        !Array.isArray(data) &&
        typeof (data as any).code === 'number' &&
        !isInnerBizSuccess((data as any).code)
      ) {
        const inner = data as { code: number; msg?: string };
        const innerMsg = inner.msg || msg || '请求失败';
        const error: any = new Error(innerMsg);
        error.name = 'BizError';
        error.info = { code: inner.code, msg: innerMsg, data };
        throw error;
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { msg } = errorInfo;
          message.error(msg);

        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      // 拦截请求配置，进行个性化处理。
      // 将 token 放在请求头中，而不是 URL 参数
      const token = localStorage.getItem('edu_token');
      const headers = {
        ...config.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      return { ...config, headers };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      // 统一处理：外层 code=200，但内层 data.code=401（登录过期）
      const maybeOuter = data as any;
      const innerCode = maybeOuter?.data?.code;
      if (maybeOuter?.code === 200 && innerCode === 401) {
        const msg = maybeOuter?.data?.msg || '登录已过期，请重新登录';
        // 避免多接口并发时弹多次
        const flagKey = '__edu_login_expired_modal_shown__';
        const alreadyShown = (window as any)[flagKey] === true;
        if (!alreadyShown) {
          (window as any)[flagKey] = true;
          Modal.confirm({
            title: '登录已过期',
            content: msg,
            okText: '去登录',
            cancelText: '取消',
            maskClosable: false,
            onOk: () => {
              try {
                localStorage.removeItem('edu_token');
                localStorage.removeItem('edu_user');
              } catch {
                // ignore
              }
              const { pathname, search } = window.location;
              const redirect = pathname + search;
              history.replace({
                pathname: '/user/login',
                search: `?redirect=${encodeURIComponent(redirect)}`,
              });
              (window as any)[flagKey] = false;
            },
            onCancel: () => {
              (window as any)[flagKey] = false;
            },
          });
        }
        // 依然返回原数据，调用方会因为跳转而停止使用该结果
        return response;
      }

      // 外层 code=200，内层为 Controller 返回的 code/msg（经 JsonResponseMiddleware 包在 data 里）
      if (
        maybeOuter?.code === 200 &&
        maybeOuter?.data != null &&
        typeof maybeOuter.data === 'object' &&
        !Array.isArray(maybeOuter.data) &&
        typeof (maybeOuter.data as any).code === 'number' &&
        !isInnerBizSuccess((maybeOuter.data as any).code)
      ) {
        const inner = maybeOuter.data as { code: number; msg?: string };
        const innerMsg = inner.msg || '请求失败';
        const error: any = new Error(innerMsg);
        error.name = 'BizError';
        error.info = { code: inner.code, msg: innerMsg, data: maybeOuter.data };
        throw error;
      }

      if (data.code !== 200) {
        message.error(data.msg);
        // 仍然把原始结构返回给调用方，避免调用方拿到 false 导致页面崩溃
        return data;
      }

      // 检测是否为 Laravel 分页格式：{current_page, data: Array, total, per_page, ...}
      // 这种格式需要转换为 ProTable 兼容格式，同时保持原格式供其他场景使用
      const maybePaginator = data.data;
      if (
        maybePaginator &&
        typeof maybePaginator === 'object' &&
        !Array.isArray(maybePaginator) &&
        'current_page' in maybePaginator &&
        'data' in maybePaginator &&
        Array.isArray((maybePaginator as any).data)
      ) {
        return {
          ...response,
          data: {
            ...(maybePaginator as any),
            data: (maybePaginator as any).data,
            success: true,
            total: (maybePaginator as any).total ?? ((maybePaginator as any).data?.length || 0),
          },
        };
      }

      // 兼容另一类常见分页结构：{ code:200, data: { data: [...], total: xxx } }
      // （无 current_page 字段，但 total 是真实总数）
      if (
        maybePaginator &&
        typeof maybePaginator === 'object' &&
        !Array.isArray(maybePaginator) &&
        Array.isArray((maybePaginator as any).data)
      ) {
        return {
          ...response,
          data: {
            ...(maybePaginator as any),
            data: (maybePaginator as any).data,
            success: true,
            total: (maybePaginator as any).total ?? ((maybePaginator as any).data?.length || 0),
          },
        };
      }

      // 如果 data.data 直接是数组，包装为 ProTable 格式
      if (Array.isArray(data.data)) {
        return {
          data: data.data,
          success: true,
          // 优先使用后端真实 total，缺失时再回退当前页长度
          total: (data as any).total ?? data.data.length,
        };
      }

      // 如果 data 本身就是数组（响应拦截器可能已经处理过），包装为 ProTable 格式
      if (Array.isArray(data)) {
        return {
          data: data,
          success: true,
          total: data.length,
        };
      }

      // 其他情况保持原样返回（向后兼容）
      return data;
    },
  ],
};
